import { useAuthMe } from '@h2hspark/auth-ui';
import { Skeleton } from '@h2hspark/ui';
import * as React from 'react';

import { InstallOfferNotice } from '../../member/pwa/install-offer-notice';

import { useMyRegistration } from './api';

/**
 * What the event page's registration region holds for a reader who is **not** the registered
 * member's header card (`MEM-017` AC-1, AC-5, AC-6; `MEM-019` AC-2).
 *
 * ## The confirmed panel is gone, on Sumit's word
 *
 * Reviewing #528 he asked to *"get rid of the You're going row"* — the card that read
 * *"You're going!"* over *"Confirmed · order #…"* with Ticket, Matches, Connect, Calendar and
 * Cancel. The 10 September header card carries every one of them now, and carried three of them
 * already, so the page was saying the same thing twice:
 *
 * | What the panel had | Where it is now |
 * | --- | --- |
 * | *You're going!* | the header card's status pill (`MEM-053` AC-4) |
 * | *Confirmed · order #…* | the ticket, which is what the order number is read off (`MEM-022` AC-1) |
 * | Ticket | *View your ticket* in the header card, which opens the ticket overlay |
 * | Matches, Connect | the Before · During · After stepper, which also says when each opens |
 * | Calendar | `AddToCalendarControl`, beside *View your ticket* |
 * | Cancel | `CancelRegistrationControl`, under the status pill |
 *
 * `MEM-017` AC-1, AC-2 and `MEM-025` AC-1 are reworded to that arrangement in this pull request,
 * as confirmed departures; the reasoning is in each story's Notes.
 *
 * ## What is left here, and why it is still one region
 *
 *   - **Signed out:** `MEM-019` AC-2's sentence. A prompt rather than a disabled button — a control
 *     that cannot work is worse than an explanation of why it is not there.
 *   - **Registered:** `PLT-008` AC-5's install offer, which used to sit at the foot of the
 *     confirmed panel. The criterion is *"once after the member's first completed registration"*,
 *     and a member reading this page is exactly that member; the offer keeps the placement rule
 *     (never before a registration, at most twice ever, never once installed) in `install-offer.ts`.
 *   - **Signed in, not registered:** nothing. `MEM-020` AC-1 makes the reserve action the pinned
 *     bar and the sidebar's button, and a second one here could disagree with it about the price.
 */
export function RegistrationPanel({
  eventId,
}: {
  readonly eventId: string;
}): React.ReactElement | null {
  const { user, loading } = useAuthMe();
  const signedIn = user !== null;
  const query = useMyRegistration(eventId, { enabled: signedIn });

  if (loading && user === null) return <Skeleton className="h-12 w-full rounded-lg" />;

  // Signed out: nothing (TW 44206362). The prototype draws no bar here, and the pinned *Reserve
  // my spot* already takes a visitor into sign-up (`MEM-058`), so a sentence saying to sign in
  // was a second, weaker version of the button beside it.
  if (!signedIn) return null;

  if (query.isPending) return null;
  if ((query.data?.registration ?? null) === null) return null;

  /*
    `PLT-008` AC-5's moment. A confirmed registration is what this branch is — a member who reaches
    it has finished the thing, which is the story's own argument for the timing: they have seen the
    product work and have a reason to want it on their home screen. A member thirty seconds into
    their first visit has neither, and answers No permanently.
  */
  return <InstallOfferNotice hasCompletedRegistration />;
}
